// Angular
import {
  Component,
  inject,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  signal,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgClass } from '@angular/common';
// PrimeNG
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
// Environment
import { environment } from '@env/environment';
// Components
import {
  AddressForm,
  AddressFormControlContainerComponent,
  AddressFormInit,
} from '@shared/components-v3/form-containers/address-form-control-container/address-form-control-container.component';
// Services
import { CheckoutService } from '../../../../../../services/checkout/checkout.service';
import { CheckoutStorageService } from 'src/app/checkout-v2/services/checkout-storage/checkout-storage.service';
import { CustomerAddressApiService } from '@core/services-v2/customer-address/customer-address-api.service';
import { SessionService } from '@core/services-v2/session/session.service';
// Directives
import { ControlErrorsDirective } from '@shared/directives-v3/validation-message/directives/control-errors/control-errors.directive';
import { FormSubmitDirective } from '@shared/directives-v3/validation-message/directives/form-submit/form-submit.directive';
// Models
import { UseDeliveryAddressOption } from './models/use-delivery-address-options.interface';
import { ControlsOf } from '@shared/models-v3/controls-of.type';
import { ISession } from '@core/models-v2/auth/session.interface';
import { AddressDto } from '@core/services-v2/customer-address/dto/create-address.dto';
import { ICustomerAddress } from '@core/models-v2/customer/customer.interface';

const NG_MODULES = [ReactiveFormsModule, NgClass];
const PRIME_MODULES = [RadioButtonModule, InputTextModule, SkeletonModule];
const COMPONENTS = [AddressFormControlContainerComponent];
const DIRECTIVES = [ControlErrorsDirective, FormSubmitDirective];

@Component({
  selector: 'app-invoice-address-form',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, COMPONENTS, DIRECTIVES],
  templateUrl: './invoice-address-form.component.html',
})
export class InvoiceAddressFormComponent implements OnChanges {
  //#region ViewChild elements
  @ViewChild('invoiceAddressFormElement')
  invoiceAdressFormElement!: ElementRef;
  //#endregion

  //#region Inputs & Outputs
  @Input() useDeliveryAddress: boolean = false;
  @Input() isLoadedBusiness: boolean = false;
  @Input() selectedAddress: ICustomerAddress | null = null;
  @Input() isRegisteredBusiness: boolean = false;
  @Output() onUseDeliveryAddressChange: EventEmitter<boolean> =
    new EventEmitter();
  //#endregion

  // FIXME:
  isLoadingAddress = signal<boolean>(true);
  useDeliveryAddressOptions: UseDeliveryAddressOption[] = [
    { title: 'La misma dirección de envío', value: true },
    { title: 'Usar dirección de facturación distinta', value: false },
  ];

  invoiceAddressForm!: FormGroup<ControlsOf<AddressFormInit | {}>>;
  useDeliveryAddressForm!: FormGroup<
    ControlsOf<{ useDeliveryAddress: boolean }>
  >;

  session!: ISession;

  //#region Getters
  get useDeliveryAddressField() {
    return this.useDeliveryAddressForm.get('useDeliveryAddress');
  }
  //#endregion

  //#region Dependencies
  public readonly checkoutService = inject(CheckoutService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly checkoutStorageService = inject(CheckoutStorageService);
  public readonly sessionService = inject(SessionService);
  public readonly customerAddressApiService = inject(
    CustomerAddressApiService
  );
  //#endregion

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['selectedAddress'] &&
      changes['selectedAddress'].currentValue
    ) {
      this.isLoadingAddress.set(false);
    }
  }

  ngOnInit(): void {
    this.session = this.sessionService.getSession();
    if (this.sessionService.isLoggedIn()) this.getInvoiceAddress();
    this.buildForm();
  }

  /**
   * Construir el formulario.
   */
  private buildForm(): void {
    this.useDeliveryAddressForm = this.fb.group({
      useDeliveryAddress: this.useDeliveryAddress,
    });
    if (this.checkoutService.shippingType() === 'PICKUP') {
      this.useDeliveryAddressForm.setValue({ useDeliveryAddress: false });
      this.onUseDeliveryAddressChange.emit(false);
    }
    this.invoiceAddressForm = this.fb.group({});
  }

  /**
   * Obtener la dirección de facturación del cliente predeterminada.
   */
  private getInvoiceAddress(): void {
    this.isLoadingAddress.set(true);
    const { documentId } = this.session;
    this.customerAddressApiService.getInvoiceAddresses(documentId).subscribe({
      next: (addresses) => {
        this.selectedAddress = addresses[0];
        this.isLoadingAddress.set(false);
      },
      error: () => {
        this.isLoadingAddress.set(false);
      },
    });
  }

  /**
   * Al cambiar la opción si se usa la dirección de envío como dirección de facturación.
   * @param value - Valor de la opción.
   */
  onSelectUseDeliveryAddress(value: boolean): void {
    this.useDeliveryAddressForm.setValue({ useDeliveryAddress: value });
    this.onUseDeliveryAddressChange.emit(value);
  }

  /**
   * Dispara el evento submit del formulario.
   */
  nativeSubmit(): void {
    if (this.invoiceAddressForm.valid) return;
    this.invoiceAddressForm.markAllAsTouched();
  }

  /**
   * Dispara el evento submit del formulario y valida su estado.
   * - Este método es llamado desde el componente padre.
   */
  submit(sendEvents: boolean = true): {
    isValid: boolean;
    invoiceAddress: AddressDto | null;
    invoiceAddressId: string | null;
  } {
    let isValid = false;
    let invoiceAddress: AddressDto | null = null;
    let invoiceAddressId: string | null = null;

    if (this.sessionService.isEnterprise()) {
      return { isValid, invoiceAddress, invoiceAddressId };
    }

    // Indico que usa dirección de despacho.
    if (this.useDeliveryAddress) {
      const deliveryAddress = this.checkoutStorageService.getDeliveryAddress();
      if (!deliveryAddress) {
        return { isValid, invoiceAddress, invoiceAddressId };
      }
      invoiceAddressId = deliveryAddress.id;
      isValid = true;
      // La empresa no esta registrada
    } else if (!this.isRegisteredBusiness) {
      if (sendEvents) {
        this.invoiceAdressFormElement.nativeElement.dispatchEvent(
          new Event('submit', { cancelable: true })
        );
      }
      isValid = this.invoiceAddressForm.valid;
      if (!isValid) return { isValid, invoiceAddress, invoiceAddressId };

      const { search, commune, ...addressFormValues } =
        this.invoiceAddressForm.getRawValue() as AddressForm;
      const [city, province, region] = commune
        ? commune.split('@')
        : ['', '', ''];
      invoiceAddress = {
        ...addressFormValues,
        city:
          environment.country == 'pe'
            ? addressFormValues.locality
            : city.toUpperCase(),
        province: province ? province.toUpperCase() : '',
        region: region ? region.toUpperCase() : '',
      };
    } else if (this.isRegisteredBusiness) {
      isValid = true;
    }

    return { isValid, invoiceAddress, invoiceAddressId };
  }
}
